import React from 'react';
import { Box, Flex, Typography, Chip, Grid } from '@dripcapital/dripui-v1';
import { DAJ_COLORS, getAccountMergeKey } from 'utils';
import { Spacer, AtomicButton } from 'atoms';
import { ButtonTypeEnum } from 'enums';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useReactiveVar } from '@apollo/client';
import { appDataVar } from 'config/server';

export type ConnectedBankAccount = {
  id: string;
  institutionName: string;
  avatarText?: string;
  isActive: boolean;
  isAuthorized: boolean;
  achStatus?: string;
  source?: string;
  accounts?: Array<{
    id: string;
    accountName: string;
    mask: string;
    accountType: string;
    achStatus: string;
    verificationMethod?: string;
    consentGiven?: boolean;
    bankDetailId?: string | number;
    plaidItemId?: string | number;
    product?: string;
  }>;
};

export interface ConnectedBankAccountsListProps {
  accounts: ConnectedBankAccount[];
  onAddNewAccount?: () => void;
  onReviewAccount?: (account: any) => void;
  onVerifyAccount?: (account: any) => void;
  onAuthorizeAccount?: (account: any) => void;
  isTransactionsReady: boolean;
  hideActionColumn?: boolean;
}

const getAchStatusDisplay = (achStatus: string, consentGiven?: boolean) => {
  const status = achStatus?.toLowerCase() || '';

  if (
    [
      'pending',
      'pending_automatic_verification',
      'pending_manual_verification',
    ].includes(status)
  ) {
    return { label: 'Pending Verification', color: 'warning' as const };
  }

  if (['verification_expired', 'verification_failed'].includes(status)) {
    return { label: 'Verification Failed', color: 'error' as const };
  }

  if (
    ['automatically_verified', 'manually_verified', 'verified'].includes(status)
  ) {
    if (consentGiven === false) {
      return { label: 'Consent Pending', color: 'warning' as const };
    }
    if (consentGiven === true) {
      return { label: 'Completed', color: 'success' as const };
    }
  }

  return { label: achStatus || 'Not Authorized', color: 'info' as const };
};

// --- Internal Functional Snippets ---

const ListHeader: React.FC<{
  onAddNewAccount?: () => void;
  isTransactionsReady: boolean;
  isBDM: boolean;
  isCP: boolean;
}> = ({ onAddNewAccount, isTransactionsReady, isBDM, isCP }) => (
  <Flex alignItems="center" justifyContent="space-between">
    <Flex direction="column" spacing={0.5}>
      <Typography variant="h3" color="secondary" fontWeight="bold">
        Connected Bank Accounts
      </Typography>
      <Typography variant="h4" fontWeight="regular" color="secondary">
        Following accounts can be authorized for ACH:
      </Typography>
    </Flex>

    <Flex alignItems="center" spacing={2}>
      {onAddNewAccount && (
        <AtomicButton
          variant={ButtonTypeEnum.TEXT}
          onClick={onAddNewAccount as any}
          disabled={!isTransactionsReady || isBDM || isCP}
          endIcon={AddIcon}
        >
          Add new account
        </AtomicButton>
      )}
    </Flex>
  </Flex>
);

const PendingVerificationAlert: React.FC = () => (
  <>
    <Box
      _sx={{
        backgroundColor: DAJ_COLORS.WHITE,
        border: `1px solid ${DAJ_COLORS.MUSTARD_YELLOW_300}`,
        borderLeft: `4px solid ${DAJ_COLORS.MUSTARD_YELLOW_300}`,
        borderRadius: '8px',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Flex direction="column" spacing={1}>
        <Typography variant="h4" fontWeight="bold" color="secondary">
          Bank account verification in progress
        </Typography>
        <Typography variant="body" color="black">
          Please log in to the portal to verify your account in 1–2 business
          days. You will receive a micro-deposit containing a 3-letter
          verification code in the transaction description for your account.
        </Typography>
      </Flex>
    </Box>
    <Spacer verticalSpace={3} />
  </>
);

const TableHeader: React.FC<{
  isReadOnly: boolean;
  hideActionColumn?: boolean;
}> = ({ isReadOnly, hideActionColumn }) => (
  <Box
    _sx={{
      backgroundColor: DAJ_COLORS.SKY_BLUE_50,
      borderBottom: `1px solid ${DAJ_COLORS.SKY_BLUE_400}`,
      padding: '16px 24px',
    }}
  >
    <Grid container alignItems="center" spacing={2}>
      <Grid item xs={2}>
        <Typography variant="label" fontWeight="bold" color="secondary">
          Bank Name
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="label" fontWeight="bold" color="secondary">
          Account Number
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="label" fontWeight="bold" color="secondary">
          Account Type
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="label" fontWeight="bold" color="secondary">
          Plaid Status
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="label" fontWeight="bold" color="secondary">
          ACH Authorization
        </Typography>
      </Grid>
      {!isReadOnly && !hideActionColumn && (
        <Grid item xs={2}>
          <Typography variant="label" fontWeight="bold" color="secondary">
            Action
          </Typography>
        </Grid>
      )}
    </Grid>
  </Box>
);

const StatusChip: React.FC<{
  account: ConnectedBankAccount;
  acc: NonNullable<ConnectedBankAccount['accounts']>[0] & {
    hasTransfer?: boolean;
  };
}> = ({ account, acc }) => {
  const isChecking =
    acc.accountType?.toLowerCase().includes('checking') ||
    acc.accountType?.toLowerCase() === 'checking';

  if (
    (account.source === 'plaid' || account.source === 'moneythumb') &&
    !acc.hasTransfer
  ) {
    if (isChecking) {
      return <Chip size="small" label="Unauthorized" color="info" />;
    }
    return <Chip size="small" label="-" color="info" />;
  }

  const { label, color } = getAchStatusDisplay(acc.achStatus, acc.consentGiven);

  return <Chip size="small" label={label} color={color as any} />;
};

const ActionButton: React.FC<{
  acc: NonNullable<ConnectedBankAccount['accounts']>[0] & {
    hasTransfer?: boolean;
  };
  account: ConnectedBankAccount;
  onReviewAccount?: (account: any) => void;
  onVerifyAccount?: (account: any) => void;
  onAuthorizeAccount?: (account: any) => void;
}> = ({
  acc,
  account,
  onReviewAccount,
  onVerifyAccount,
  onAuthorizeAccount,
}) => {
  const isChecking =
    acc.accountType?.toLowerCase().includes('checking') ||
    acc.accountType?.toLowerCase() === 'checking';

  if (
    (account.source === 'plaid' || account.source === 'moneythumb') &&
    !acc.hasTransfer
  ) {
    if (isChecking && onAuthorizeAccount) {
      return (
        <AtomicButton
          variant={ButtonTypeEnum.TEXT}
          onClick={(e) => {
            e.stopPropagation();
            onAuthorizeAccount(acc);
          }}
          endIcon={ChevronRightIcon}
          _sx={{
            padding: '8px 12px',
            minWidth: 'auto',
            fontSize: '12px!important',
            fontWeight: 'bold',
          }}
        >
          Authorize for ACH
        </AtomicButton>
      );
    }
    return null;
  }

  const isReviewable = acc.achStatus === 'verified' && !acc.consentGiven;
  const isVerifiable =
    acc.achStatus === 'pending' &&
    (acc.verificationMethod === 'manually_deposit' ||
      acc.verificationMethod === 'manual_microdeposit');

  const isDisabled = !(isReviewable || isVerifiable);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isReviewable && onReviewAccount) {
      onReviewAccount(acc);
    } else if (isVerifiable && onVerifyAccount) {
      onVerifyAccount(acc);
    }
  };

  let buttonText = 'Authorize for ACH';
  if (isReviewable) buttonText = 'Provide Consent';
  if (isVerifiable) buttonText = 'Enter Code';

  return (
    <AtomicButton
      variant={ButtonTypeEnum.TEXT}
      disabled={isDisabled}
      onClick={handleClick}
      endIcon={ChevronRightIcon}
      _sx={{
        padding: '8px 12px',
        minWidth: 'auto',
        fontSize: '12px!important',
        fontWeight: 'bold',
      }}
    >
      {buttonText}
    </AtomicButton>
  );
};

const AccountRow: React.FC<{
  account: ConnectedBankAccount;
  acc: NonNullable<ConnectedBankAccount['accounts']>[0] & {
    hasTransfer?: boolean;
  };
  isFirst: boolean;
  isReadOnly: boolean;
  hideActionColumn?: boolean;
  onReviewAccount?: (account: any) => void;
  onVerifyAccount?: (account: any) => void;
  onAuthorizeAccount?: (account: any) => void;
}> = ({
  account,
  acc,
  isFirst,
  isReadOnly,
  hideActionColumn,
  onReviewAccount,
  onVerifyAccount,
  onAuthorizeAccount,
}) => (
  <Box _sx={{ padding: '8px 0' }}>
    <Grid container alignItems="center" spacing={2}>
      <Grid item xs={2}>
        {isFirst && (
          <Typography variant="body" color="secondary">
            {account.institutionName}
          </Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body" color="secondary">
          ********{acc.mask}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body"
          color="secondary"
          _sx={{ textTransform: 'capitalize' }}
        >
          {acc.accountType || '-'}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Chip
          size="small"
          color={account.isActive ? 'success' : 'error'}
          label={account.isActive ? 'Connection Active' : 'Disconnected'}
        />
      </Grid>
      <Grid item xs={2}>
        <StatusChip account={account} acc={acc} />
      </Grid>
      {!isReadOnly && !hideActionColumn && (
        <Grid item xs={2}>
          <ActionButton
            acc={acc}
            account={account}
            onReviewAccount={onReviewAccount}
            onVerifyAccount={onVerifyAccount}
            onAuthorizeAccount={onAuthorizeAccount}
          />
        </Grid>
      )}
    </Grid>
  </Box>
);

// --- Main Component ---

const ConnectedBankAccountsList: React.FC<ConnectedBankAccountsListProps> = ({
  accounts,
  onAddNewAccount,
  onReviewAccount,
  onVerifyAccount,
  onAuthorizeAccount,
  isTransactionsReady,
  hideActionColumn,
}) => {
  const { isChannelPartnerVar: isCP, isBDMVar: isBDM } =
    useReactiveVar(appDataVar);

  const isReadOnly = isBDM || isCP;

  const hasPendingMicrodeposit = accounts.some((account) =>
    account.accounts?.some(
      (acc) =>
        acc.achStatus === 'pending' &&
        (acc.verificationMethod === 'manually_deposit' ||
          acc.verificationMethod === 'manual_microdeposit')
    )
  );

  return (
    <Box _sx={{ width: '100%', maxWidth: '912px' }}>
      <ListHeader
        onAddNewAccount={onAddNewAccount}
        isTransactionsReady={isTransactionsReady}
        isBDM={isBDM}
        isCP={isCP}
      />

      <Spacer verticalSpace={3} />

      {hasPendingMicrodeposit && <PendingVerificationAlert />}

      <Box
        _sx={{
          backgroundColor: DAJ_COLORS.WHITE,
          border: `1px solid ${DAJ_COLORS.SKY_BLUE_400}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <TableHeader
          isReadOnly={isReadOnly}
          hideActionColumn={hideActionColumn}
        />

        <Box _sx={{ padding: '0px 24px' }}>
          {accounts.length === 0 ? (
            <Box _sx={{ padding: '32px 0', textAlign: 'center' }}>
              <Typography variant="body" color="secondary">
                No bank accounts connected yet.
              </Typography>
            </Box>
          ) : (
            accounts.map((account, index) => {
              const isLastBank = index === accounts.length - 1;

              const mergedAccountsMap = new Map<
                string,
                NonNullable<ConnectedBankAccount['accounts']>[0] & {
                  hasTransfer?: boolean;
                }
              >();

              account.accounts?.forEach((acc) => {
                // Same mask + bank name merge across sources (Plaid / MoneyThumb).
                const key = getAccountMergeKey(acc.mask);
                if (!key) {
                  // No mask — keep as its own row keyed by account id.
                  mergedAccountsMap.set(acc.id, {
                    ...acc,
                    hasTransfer: acc.product === 'transfer',
                  });
                  return;
                }
                if (!mergedAccountsMap.has(key)) {
                  mergedAccountsMap.set(key, {
                    ...acc,
                    hasTransfer: acc.product === 'transfer',
                  });
                } else if (acc.product === 'transfer') {
                  // Prefer transfer account when both products/sources exist.
                  mergedAccountsMap.set(key, {
                    ...acc,
                    hasTransfer: true,
                  });
                }
              });

              const mergedAccountsList = Array.from(mergedAccountsMap.values());

              return (
                <Box
                  key={account.id}
                  _sx={{
                    borderBottom: isLastBank
                      ? 'none'
                      : `1px solid ${DAJ_COLORS.NOBLE_BLUE}`,
                    padding: '12px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  {mergedAccountsList.map((acc, accIndex) => {
                    const isFirst = accIndex === 0;
                    return (
                      <AccountRow
                        key={acc.id}
                        account={account}
                        acc={acc}
                        isFirst={isFirst}
                        isReadOnly={isReadOnly}
                        hideActionColumn={hideActionColumn}
                        onReviewAccount={onReviewAccount}
                        onVerifyAccount={onVerifyAccount}
                        onAuthorizeAccount={onAuthorizeAccount}
                      />
                    );
                  })}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ConnectedBankAccountsList;
